import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext.jsx';
import { useUser } from '../hooks/useUsers';
import { updatePortfolio } from '../data/userService';
import Donut from '../components/Donut.jsx';
import PieLegend from '../components/PieLegend.jsx';
import { deriveAssetKey, formatPct, holdingColor, pieLabel, toNumber, totalPct } from '../utils/portfolio';
import './UpdatePortfolioPage.css';

/** Enter holdings and their %; the pie redraws as you type. Saves only at 100%. */
export default function UpdatePortfolioPage() {
  const { currentUid, refresh } = useSession();
  const { user } = useUser(currentUid);
  const navigate = useNavigate();
  const nextId = useRef(0);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const newRow = (h = {}) => ({
    id: nextId.current++,
    label: h.label || '',
    ticker: h.ticker || '',
    pct: h.pct != null ? String(h.pct) : '',
    why: h.why || '',
  });

  useEffect(() => {
    if (user && !draft) {
      const p = user.portfolio || {};
      setDraft({
        rows: (p.holdings || []).map(newRow),
        value: p.value != null ? String(p.value) : '',
        return1y: p.return1y != null ? String(p.return1y) : '',
        showValue: p.showValue ?? true,
      });
    }
  }, [user, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !draft) return <main className="page"><p className="muted">Loading…</p></main>;

  const total = totalPct(draft.rows);
  const isComplete = Math.abs(total - 100) < 0.05;
  const status = isComplete
    ? 'Adds up to 100%. Ready to save.'
    : total < 100
      ? `${formatPct(100 - total)} left to allocate`
      : `Over by ${formatPct(total - 100)}`;

  const setRow = (id, field) => (e) =>
    setDraft({ ...draft, rows: draft.rows.map((r) => (r.id === id ? { ...r, [field]: e.target.value } : r)) });
  const removeRow = (id) => setDraft({ ...draft, rows: draft.rows.filter((r) => r.id !== id) });
  const addRow = () => setDraft({ ...draft, rows: [...draft.rows, newRow()] });
  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  const save = async () => {
    if (!isComplete) return;
    setSaving(true);
    const holdings = draft.rows
      .filter((r) => (r.label || r.ticker) && toNumber(r.pct) > 0)
      .map((r) => {
        const ticker = r.ticker.trim().toUpperCase();
        const label = r.label.trim() || ticker;
        return { label, ticker, assetKey: deriveAssetKey({ name: label, ticker }), pct: toNumber(r.pct), why: r.why.trim() };
      });
    await updatePortfolio(currentUid, {
      holdings,
      value: toNumber(draft.value),
      return1y: toNumber(draft.return1y),
      showValue: draft.showValue,
      updatedAt: new Date().toISOString(),
    });
    refresh();
    navigate(`/profile/${currentUid}`);
  };

  return (
    <main className="page">
      <Link to={`/profile/${currentUid}`} className="back-link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        My profile
      </Link>
      <div>
        <h1 className="page-title">Update your portfolio</h1>
        <p className="muted">Enter each holding and its share of your portfolio. Your pie redraws as you type.</p>
      </div>

      <div className="editor">
        <div className="editor__rows">
          {draft.rows.map((r, i) => (
            <div key={r.id} className="card editor__row">
              <span className="editor__dot" style={{ background: holdingColor(r, i) }} aria-hidden="true" />
              <div className="editor__fields">
                <div className="editor__line">
                  <div className="field editor__name">
                    <label htmlFor={`h-name-${r.id}`}>Fund or stock</label>
                    <input id={`h-name-${r.id}`} className="input" placeholder="e.g. Vanguard FTSE All-World" value={r.label} onChange={setRow(r.id, 'label')} />
                  </div>
                  <div className="field editor__ticker">
                    <label htmlFor={`h-ticker-${r.id}`}>Ticker</label>
                    <input id={`h-ticker-${r.id}`} className="input editor__upper" placeholder="VWRP" value={r.ticker} onChange={setRow(r.id, 'ticker')} />
                  </div>
                  <div className="field editor__pct">
                    <label htmlFor={`h-pct-${r.id}`}>% of pie</label>
                    <input id={`h-pct-${r.id}`} className="input editor__right" type="number" inputMode="decimal" min="0" max="100" step="0.5" value={r.pct} onChange={setRow(r.id, 'pct')} />
                  </div>
                  <button type="button" className="editor__remove" aria-label={`Remove ${r.ticker || r.label || 'this holding'}`} onClick={() => removeRow(r.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                </div>
                <div className="field">
                  <label htmlFor={`h-why-${r.id}`}>
                    Why do you hold it? <span className="editor__optional">(optional, but it's what people come for)</span>
                  </label>
                  <textarea id={`h-why-${r.id}`} className="input" rows={2} placeholder="A sentence or two on why it's in your portfolio." value={r.why} onChange={setRow(r.id, 'why')} />
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="btn btn--ghost editor__add" onClick={addRow}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Add a holding
          </button>

          <section className="card editor__numbers" aria-labelledby="numbers-title">
            <h2 id="numbers-title" className="editor__numbers-title">Numbers on your profile</h2>
            <div className="editor__line">
              <div className="field editor__grow">
                <label htmlFor="pf-value">Total value (£)</label>
                <input id="pf-value" className="input" inputMode="decimal" value={draft.value} onChange={set('value')} />
              </div>
              <div className="field editor__grow">
                <label htmlFor="pf-return">1-year return (%)</label>
                <input id="pf-return" className="input" inputMode="decimal" value={draft.return1y} onChange={set('return1y')} />
              </div>
            </div>
            <label className="editor__check">
              <input type="checkbox" checked={draft.showValue} onChange={() => setDraft({ ...draft, showValue: !draft.showValue })} />
              Show my £ value. Leave unticked to share percentages and return only.
            </label>
          </section>
        </div>

        <aside className="card editor__pie" aria-labelledby="pie-title">
          <h2 id="pie-title" className="section-title">Your pie</h2>
          <div className="editor__donut">
            <Donut holdings={draft.rows} size={190} ring={28} label={pieLabel('Draft', draft.rows)}>
              <span className="donut__big">{formatPct(total)}</span>
              <span className="donut__small">allocated</span>
            </Donut>
          </div>
          <p role="status" className={`editor__status${isComplete ? ' editor__status--ok' : ''}`}>{status}</p>
          <PieLegend holdings={draft.rows} />
          <button type="button" className="btn btn--primary" disabled={!isComplete || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save portfolio'}
          </button>
          <Link to={`/profile/${currentUid}`} className="btn btn--ghost">Cancel</Link>
        </aside>
      </div>
    </main>
  );
}
